"use client";

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { Pin, PinOff } from "lucide-react";
import { likesAPI, sharesAPI, postsAPI, channelsAPI, handleApiError, socialUtils } from "../../services/api";
import { getUserTypeColor } from "../../utils/userTypeUtils";
import { getUserPlaceholderImage } from "../../utils/imageUtils";
import ChannelPostReactions from "./ChannelPostReactions";
import ConfirmModal from "./modals/ConfirmModal";
import ChannelPostComments from "./ChannelPostComments";
import PostReactionsModal from "../social/posts/PostReactionsModal";
import AdoptionPostEmbed from "../social/posts/AdoptionPostEmbed";
import { usePostModal } from "../../contexts/PostModalContext";
import MultiMediaDisplay from "../social/posts/MultiMediaDisplay";
import {
  showErrorToast,
  showSuccessToast,
  showInfoToast
} from "../../utils/notificationUtils";
import "./ChannelPost.css";

const ChannelPost = ({ post, channel, userRole, onEditPost, onDeletePost, onChannelPinnedChange, onUpdate }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getUserType, getProfileImageUrl, user: currentUser } = useAuth();
  const { openPostModal } = usePostModal();

  // Normalize media from post (supports both post.media and post.images)
  const postMedia = useMemo(() => {
    const raw = post?.media || post?.images || []
    if (!Array.isArray(raw) || raw.length === 0) return []
    return raw.map(item => {
      if (typeof item === 'string') return item
      return {
        url: item.url || item,
        type: item.type || item.file_type,
        thumbnail: item.thumbnail,
        resolutions: item.resolutions
      }
    })
  }, [post])

  const [showComments, setShowComments] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showReactionsModal, setShowReactionsModal] = useState(false);

  // Get current user type color
  const currentUserType = getUserType();
  const userTypeColor = getUserTypeColor(currentUserType);

  const isOwner = useMemo(() => {
    if (!currentUser?.id || !post.author?.id) return false;
    return Number(currentUser.id) === Number(post.author.id);
  }, [currentUser?.id, post.author?.id]);

  const isChannelAdmin =
    userRole === "owner" ||
    userRole === "admin" ||
    currentUserType === "ADMIN" ||
    currentUserType === "MODERATOR";

  const isAuthorChannelOwner = useMemo(() => {
    if (!post.author?.id || !channel?.owner?.id) return false;
    return Number(post.author.id) === Number(channel.owner.id);
  }, [post.author?.id, channel?.owner?.id]);

  const canManagePost = useMemo(() => {
    if (isOwner) return true;
    if (userRole === "owner") return true;
    if (userRole === "admin") return !isAuthorChannelOwner;
    if (currentUserType === "ADMIN" || currentUserType === "MODERATOR") return true;
    return false;
  }, [isOwner, userRole, isAuthorChannelOwner, currentUserType]);

  const pollOptions = useMemo(() => {
    const options = Array.isArray(post.pollOptions)
      ? post.pollOptions
      : Array.isArray(post.poll_options)
        ? post.poll_options
        : [];

    return options.map((option) => {
      if (option.votes !== undefined) {
        return option;
      }

      if (option.votes_count !== undefined) {
        return { ...option, votes: option.votes_count };
      }

      return { ...option, votes: 0 };
    });
  }, [post.pollOptions, post.poll_options]);

  const totalPollVotes = useMemo(() => {
    if (typeof post.poll_total_votes === "number") {
      return post.poll_total_votes;
    }

    return pollOptions.reduce(
      (sum, option) => sum + (option.votes || option.votes_count || 0),
      0
    );
  }, [post.poll_total_votes, pollOptions]);

  const isPollExpired = post.poll_ended || (post.poll_expires_at && new Date(post.poll_expires_at) < new Date());

  const normalizedType = (() => {
    const rawType = (
      post.type ||
      post.post_type ||
      post.postType ||
      ""
    )
      .toString()
      .toLowerCase();

    if (["question", "poll", "story"].includes(rawType)) {
      return rawType;
    }

    if (post.is_question) {
      return "question";
    }

    const hasPollOptions =
      (Array.isArray(post.pollOptions) && post.pollOptions.length > 0) ||
      (Array.isArray(post.poll_options) && post.poll_options.length > 0);
    if (hasPollOptions) {
      return "poll";
    }

    if (post.is_story || post.story_id) {
      return "story";
    }

    return null;
  })();

  const [isSubmittingVote, setIsSubmittingVote] = useState(false);
  const [userVoteOptionId, setUserVoteOptionId] = useState(
    post.poll_user_vote_option_id || post.poll_user_vote_id || null
  );
  const [selectedOptionId, setSelectedOptionId] = useState(
    post.poll_user_vote_option_id || post.poll_user_vote_id || null
  );

  useEffect(() => {
    const latestVoteId =
      post.poll_user_vote_option_id || post.poll_user_vote_id || null;
    setUserVoteOptionId(latestVoteId);

    if (latestVoteId !== null) {
      setSelectedOptionId(latestVoteId);
    }
  }, [post.poll_user_vote_option_id, post.poll_user_vote_id]);

  const hasManagePrivileges = userRole === "owner" || userRole === "admin";

  const normalizeId = (id) => {
    const numeric = Number(id);
    return Number.isNaN(numeric) ? id : numeric;
  };

  const isArabicText = (text) => {
    if (!text) return false;
    const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
    return arabicRegex.test(text);
  };

  const unescapeHTML = (html) => {
    if (!html) return "";
    const entities = {
      "&lt;": "<",
      "&gt;": ">",
      "&amp;": "&",
      "&quot;": '"',
      "&#39;": "'",
      "&nbsp;": " ",
    };
    return html.replace(/&lt;|&gt;|&amp;|&quot;|&#39;|&nbsp;/g, (m) => entities[m]);
  };

  const formatContent = (content) => {
    if (!content) return "";

    // If the content looks like escaped HTML (starts with &lt; or contains &gt;), unescape it first
    let processedContent = content;
    if (content.includes("&lt;") || content.includes("&gt;") || content.includes("&amp;")) {
      processedContent = unescapeHTML(content);
    }

    const isArabic = isArabicText(processedContent);

    // Robustly linkify URLs while preserving existing HTML tags
    const urlRegex = /(<a\b[^>]*>[\s\S]*?<\/a>)|(<[^>]+>)|(https?:\/\/[^\s<]+)/gi;
    const linkifiedContent = processedContent.replace(urlRegex, (match, aTag, otherTag, url) => {
      if (aTag) return aTag;
      if (otherTag) return otherTag;
      if (url) {
        const displayUrl = socialUtils.truncateUrl(url);
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #00D4B3; text-decoration: underline; word-break: break-all;" onclick="event.stopPropagation()">${displayUrl}</a>`;
      }
      return match;
    });

    // Check if originally the content had HTML tags
    const hasHTML = /<[^>]+>/.test(processedContent);

    if (hasHTML) {
      return (
        <div
          className="channel-post-html-content"
          style={{
            direction: isArabic ? "rtl" : "ltr",
            textAlign: isArabic ? "right" : "left",
            lineHeight: "1.5",
          }}
          dangerouslySetInnerHTML={{ __html: linkifiedContent }}
        />
      );
    }

    return linkifiedContent.split("\n").map((line, index) => {
      return (
        <div
          key={index}
          style={{
            direction: isArabic ? "rtl" : "ltr",
            textAlign: isArabic ? "right" : "left",
            marginBottom: line.trim() ? "8px" : "4px",
          }}
        >
          <span dangerouslySetInnerHTML={{ __html: line || "\u00A0" }} />
        </div>
      );
    });
  };

  const pinnedIds = Array.isArray(channel?.pinnedPostIds)
    ? channel.pinnedPostIds
    : [];

  const isPinned =
    post.isPinned ||
    post.is_pinned ||
    post.pinned ||
    (channel && pinnedIds.some((pid) => normalizeId(pid) === normalizeId(post.id)));

  const handleSelectPollOption = (optionId) => {
    if (isPollExpired) return;
    setSelectedOptionId(optionId);
  };

  const handleConfirmPollVote = async () => {
    if (
      isPollExpired ||
      !selectedOptionId ||
      selectedOptionId === userVoteOptionId ||
      isSubmittingVote
    ) {
      return;
    }

    const previousOptionId = userVoteOptionId;
    const previousPost = { ...post };

    // 1. Calculate next state optimistically
    const nextPollOptions = pollOptions.map((option, index) => {
      const optionId = option.id || option.option_id || index;
      const currentVotes = option.votes ?? option.votes_count ?? 0;
      let newVotes = currentVotes;

      // Use String() for safe comparison of IDs that might be numeric or strings
      const isSelected = String(optionId) === String(selectedOptionId);
      const isPrevious = previousOptionId !== null && String(optionId) === String(previousOptionId);

      if (previousOptionId === null) {
        if (isSelected) newVotes = currentVotes + 1;
      } else {
        if (isSelected) newVotes = currentVotes + 1;
        if (isPrevious) newVotes = Math.max(currentVotes - 1, 0);
      }

      return {
        ...option,
        votes: newVotes,
        votes_count: newVotes
      };
    });

    // Calculate total votes based on the summed options to ensure consistency
    const nextTotalVotes = nextPollOptions.reduce((sum, opt) => sum + opt.votes, 0);

    // 2. Update UI instantly
    setUserVoteOptionId(selectedOptionId);
    onUpdate({
      ...post,
      pollOptions: nextPollOptions,
      poll_options: nextPollOptions,
      poll_total_votes: nextTotalVotes,
      poll_user_vote_option_id: selectedOptionId,
      poll_user_vote_id: selectedOptionId,
    });

    setIsSubmittingVote(true);

    try {
      await postsAPI.voteOnPoll(post.id, selectedOptionId);
      // Success - state is already updated optimistically
    } catch (error) {
      // Revert on error
      setUserVoteOptionId(previousOptionId);
      onUpdate(previousPost);
      handleApiError(error);
    } finally {
      setIsSubmittingVote(false);
    }
  };

  const handleTogglePin = async () => {
    if (!channel?.id || !post?.id) return;

    // Only owner can pin/unpin
    if (userRole !== "owner") {
      showErrorToast(t("channelPost.ownerOnlyPin", "Only the community owner can pin posts"));
      return;
    }

    // Limit check for pinning new posts
    if (!isPinned && pinnedIds.length >= 5) {
      showErrorToast(t("channelPost.pinLimitReached", "You can only pin up to 5 posts per community"));
      return;
    }

    try {
      const response = isPinned
        ? await channelsAPI.unpinPost(channel.id, post.id)
        : await channelsAPI.pinPost(channel.id, post.id);

      const responseData = response.data?.data;
      
      if (onChannelPinnedChange) {
        onChannelPinnedChange({
          post: responseData,
          isPinned: !isPinned
        });
      }

      onUpdate({
        ...post,
        isPinned: !isPinned,
      });

      const action = !isPinned ? t("channelPost.pinned", "Pinned") : t("channelPost.unpinned", "Unpinned");
      showInfoToast(t("channelPost.pinSuccess", "{{action}} post successfully", { action }));
    } catch (error) {
      handleApiError(error);
    }
  };

  const postTypeMeta = (() => {
    switch (normalizedType) {
      case "question":
        return {
          key: "question",
          label: t("channelPost.types.question", "Question"),
          icon: (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
              <circle cx="12" cy="17" r="1"></circle>
            </svg>
          ),
        };
      case "poll":
        return {
          key: "poll",
          label: t("channelPost.types.poll", "Poll"),
          icon: (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="20" x2="18" y2="10"></line>
              <line x1="12" y1="20" x2="12" y2="4"></line>
              <line x1="6" y1="20" x2="6" y2="14"></line>
            </svg>
          ),
        };
      case "story":
        return {
          key: "story",
          label: t("channelPost.types.story", "Story"),
          icon: (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
            </svg>
          ),
        };
      default:
        return null;
    }
  })();

  const handleLike = async () => {
    try {
      // Use existing likes API
      const response = await likesAPI.toggleLike(post.id);

      if (response.data) {
        const updatedPost = {
          ...post,
          isLiked: response.data.is_liked || !post.isLiked,
          likes: response.data.likes_count || (post.isLiked ? post.likes - 1 : post.likes + 1),
          user_reaction_type: response.data.user_reaction_type || post.user_reaction_type || null
        };
        onUpdate(updatedPost);
      }
    } catch (error) {
    }
  };

  // Handle reaction update from ChannelPostReactions
  const handleReactionUpdate = (updatedPost) => {
    onUpdate(updatedPost);
  };

  const copyTextToClipboard = async (text) => {
    try {
      if (navigator?.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (err) {
      // fall through
    }

    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.top = "-9999px";
      textarea.style.left = "-9999px";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const successful = document.execCommand("copy");
      document.body.removeChild(textarea);
      return successful;
    } catch (err) {
      return false;
    }
  };

  const handleShare = async () => {
    try {
      // Use existing shares API
      const response = await sharesAPI.sharePost(post.id);
      const responseData = response?.data?.data || response?.data || {};
      
      // Get share URL from backend response (preferred) or fallback to constructing it
      const shareUrl = responseData?.share_url || responseData?.url || 
        `${window.location.origin}/social/profile/posts/${post.id}`;
      
      // Copy URL to clipboard
      const didCopy = await copyTextToClipboard(shareUrl);
      if (didCopy) {
        showSuccessToast(t("postFeed.shareActions.copySuccess") || "Link copied to clipboard!");
      } else {
        showErrorToast(t("postFeed.shareActions.copyError") || "Failed to copy link");
        window.prompt(t("postFeed.shareActions.manualCopyPrompt") || "Copy this link:", shareUrl);
      }

      // Update share count from backend response
      const shareCount = responseData?.shares_count || responseData?.shares || 
        ((post.shares || 0) + 1);

      onUpdate({
        ...post,
        shares: shareCount
      });
    } catch (error) {
      console.error("Error sharing post:", error);
      // Even if API fails, try to copy a fallback URL
      const fallbackUrl = `${window.location.origin}/social/profile/posts/${post.id}`;
      try {
        const didCopy = await copyTextToClipboard(fallbackUrl);
        if (didCopy) {
          showSuccessToast(t("postFeed.shareActions.copySuccess") || "Link copied to clipboard!");
        } else {
          showErrorToast(t("postFeed.shareActions.copyError") || "Failed to copy link");
        }
      } catch (copyErr) {
        showErrorToast(t("postFeed.shareActions.copyError") || "Failed to copy link");
      }
    }
  };

  const handleComment = () => {
    setShowComments(!showComments);
  };

    const renderAdoptionEmbed = () => {
      const adoptionPostId = post.adoption_post_id ||
        post.adoption_post?.id ||
        post.metadata?.adoption_post_id ||
        post.metadata?.adoption_post?.id ||
        (post.post_type === 'adoption' ? post.id : null);

      const adoptionPostData = post.adoption_post ||
        post.metadata?.adoption_post ||
        null;

      if (!adoptionPostId) return null;

      return (
        <div style={{ marginTop: "16px", marginBottom: "16px" }} onClick={(e) => e.stopPropagation()}>
          <AdoptionPostEmbed
            adoptionPostId={Number(adoptionPostId)}
            adoptionPost={adoptionPostData}
            currentUser={currentUser}
          />
        </div>
      );
    };

  const renderPostContent = () => {
    const mainContent = (() => {
      switch (normalizedType) {
        case "image":
        case "text":
        case "post": // Combined post type
          return (
            <div className="channel-post-content">
              {post.content && (
                <div className="channel-post-text">
                  {formatContent(post.content)}
                </div>
              )}
              {postMedia.length > 0 && (
                <MultiMediaDisplay media={postMedia} slideHeight={240} />
              )}
            </div>
          );

        case "question":
          return (
            <div className="channel-post-content">
              <div className="channel-post-question">
                <div className="channel-post-question-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                    <circle cx="12" cy="17" r="1"></circle>
                  </svg>
                </div>
                <div className="channel-post-text">
                  {formatContent(post.content)}
                </div>
              </div>
              {postMedia.length > 0 && (
                <div style={{ marginTop: "12px" }}>
                  <MultiMediaDisplay media={postMedia} slideHeight={240} />
                </div>
              )}
            </div>
          );

        case "poll":
          return (
            <div className="channel-post-content channel-post-poll">
              {post.content && (
                <div className="channel-post-text">
                  {formatContent(post.content)}
                </div>
              )}
              {pollOptions.length > 0 && (
                <div className="channel-post-poll-options" onClick={(e) => e.stopPropagation()}>
                  {pollOptions.map((option, index) => {
                    const voteCount = option.votes || option.votes_count || 0;
                    const percentage =
                      totalPollVotes > 0 ? (voteCount / totalPollVotes) * 100 : 0;
                    const optionId = option.id || option.option_id || index;
                    const isCurrentVote = userVoteOptionId === optionId;
                    const isSelected = selectedOptionId === optionId;
                    const isPendingSelection =
                      isSelected && selectedOptionId !== userVoteOptionId;
                    const optionClasses = [
                      "channel-post-poll-option",
                      isSelected ? "selected" : "",
                      isCurrentVote ? "voted" : "",
                      isPendingSelection ? "pending" : "",
                      userVoteOptionId || isPollExpired ? "active-results" : "",
                    ]
                      .filter(Boolean)
                      .join(" ");

                    return (
                      <button
                        type="button"
                        key={optionId}
                        className={optionClasses}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectPollOption(optionId);
                        }}
                        disabled={isSubmittingVote || isPollExpired}
                      >
                        <div className="poll-option-container">
                          <div
                            className="poll-option-fill"
                            style={{ width: `${percentage}%` }}
                          ></div>
                          <div className="poll-option-content">
                            <span className="poll-option-text">
                              {option.text || option.option || option.label || option}
                            </span>
                            <div className="poll-option-stats">
                              <span className="poll-option-percentage">
                                {Math.round(percentage)}%
                              </span>
                              {isCurrentVote && (
                                <span className="poll-option-voted-badge">
                                  {t("channelPost.yourVote", "Your vote")}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {isSelected && !userVoteOptionId && (
                          <div className="poll-option-checkmark">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="channel-post-poll-footer" onClick={(e) => e.stopPropagation()}>
                <div className="channel-post-poll-totals">
                  {t("channelPost.pollTotalVotes", "{{count}} total votes", {
                    count: totalPollVotes,
                  })}
                </div>
                {!isPollExpired && (
                  <button
                    type="button"
                    className="channel-post-poll-submit"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleConfirmPollVote();
                    }}
                    disabled={
                      isSubmittingVote ||
                      !selectedOptionId ||
                      selectedOptionId === userVoteOptionId
                    }
                  >
                    {isSubmittingVote
                      ? t("channelPost.pollSubmitting", "Submitting...")
                      : userVoteOptionId
                        ? t("channelPost.pollUpdateVote", "Update vote")
                        : t("channelPost.pollSubmitVote", "Submit vote")}
                  </button>
                )}
              </div>
              {post.poll_expires_at && (
                <div className="channel-post-poll-meta">
                  {isPollExpired
                    ? t("channelPost.pollEnded", "Poll ended")
                    : t("channelPost.pollExpires", "Poll closes {{date}}", {
                        date: new Date(post.poll_expires_at).toLocaleString(),
                      })}
                </div>
              )}
            </div>
          );

        case "story":
          return (
            <div className="channel-post-content channel-post-story">
              <div className="channel-post-story-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                </svg>
              </div>
              <div className="channel-post-text">
                {formatContent(post.content)}
              </div>
            </div>
          );

        default: // fallback for any other types
          return (
            <div className="channel-post-content">
              {post.content && (
                <div className="channel-post-text">
                  {formatContent(post.content)}
                </div>
              )}
              {postMedia.length > 0 && (
                <MultiMediaDisplay media={postMedia} slideHeight={240} />
              )}
            </div>
          );
      }
    })();

    return (
      <>
        {mainContent}
        {renderAdoptionEmbed()}
      </>
    );
  };

  return (
    <div
      className={`channel-post${postTypeMeta ? ` channel-post--${postTypeMeta.key}` : ""}`}
      style={{ '--user-type-color': userTypeColor }}
    >
      <div className="channel-post-header">
        <div className="channel-post-author">
          <img
            src={post.author.profileImage || post.author.avatar || getUserPlaceholderImage(post.author)}
            alt={post.author.name}
            className="channel-post-author-img"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/social/profile/${post.author.id || post.author.username}`);
            }}
            onError={(e) => {
              e.target.src = getUserPlaceholderImage(post.author);
            }}
          />
          <div className="channel-post-author-info">
            <span className="channel-post-author-name">{post.author.name}</span>
            {post.author.username && post.author.username !== post.author.name && (
              <span className="channel-post-author-username">{post.author.username}</span>
            )}
            <span className="channel-post-time">{post.createdAt}</span>
          </div>
        </div>
        <div className="channel-post-header-right">
          {isPinned && (
            <div className="channel-post-pinned-label">
              <Pin size={14} style={{ transform: "rotate(45deg)" }} />
              <span>{t("channelPost.pinned", "Pinned")}</span>
            </div>
          )}
          
          {/* Action Menu (Pin, Edit, Delete) */}
          {canManagePost && (
            <div className="channel-post-header-actions" style={{ position: "relative" }}>
              <button
                className="channel-post-action-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowDropdown(!showDropdown);
              }}
              title={t("channelPost.actions", "Actions")}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
              </svg>
            </button>

            {showDropdown && (
              <div className="channel-post-dropdown" onClick={(e) => e.stopPropagation()}>
                {/* Pin Action (Owner only) */}
                {userRole === "owner" && (
                  <button
                    className="channel-dropdown-item"
                    onClick={() => {
                      handleTogglePin();
                      setShowDropdown(false);
                    }}
                  >
                    {isPinned ? (
                      <PinOff width="18" height="18" style={{ transform: "rotate(45deg)" }} />
                    ) : (
                      <Pin width="18" height="18" style={{ transform: "rotate(45deg)" }} />
                    )}
                    <span>{isPinned ? t("channelPost.unpin", "Unpin") : t("channelPost.pin", "Pin")}</span>
                  </button>
                )}

                {/* Edit Action (Owner only) */}
                {isOwner && (
                  <button
                    className="channel-dropdown-item"
                    onClick={() => {
                      onEditPost(post);
                      setShowDropdown(false);
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    <span>{t("common.edit", "Edit")}</span>
                  </button>
                )}

                {/* Delete Action (Owner or Admin - except Admins on Owner's posts) */}
                {(isOwner || (isChannelAdmin && !(userRole === "admin" && isAuthorChannelOwner))) && (
                  <button
                    className="channel-dropdown-item channel-dropdown-item--delete"
                    onClick={() => {
                      onDeletePost(post);
                      setShowDropdown(false);
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                    <span>{t("common.delete", "Delete")}</span>
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>

      {renderPostContent()}

      <div className="channel-post-footer" onClick={(e) => e.stopPropagation()}>
        <ChannelPostReactions
          post={post}
          onLike={handleReactionUpdate}
          onShare={handleShare}
          onComment={handleComment}
          onShowReactions={() => setShowReactionsModal(true)}
        />
      </div>

      {showComments && (
        <ChannelPostComments
          post={post}
          onCommentAdded={(comment) => {
            // Optimistic update of comment count
            // Note: In a real system, the back-end usually returns the updated post or we fetch it
            if (post.comments !== undefined) {
              post.comments += 1;
            } else if (post.reactions?.comments !== undefined) {
              post.reactions.comments += 1;
            }
          }}
        />
      )}


      {showReactionsModal && (
        <PostReactionsModal
          isOpen={showReactionsModal}
          onClose={() => setShowReactionsModal(false)}
          postId={post.id}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

export default ChannelPost;

